package pl.zar.tikthrow.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import io.ktor.client.HttpClient
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import kotlin.time.Clock
import kotlinx.coroutines.launch
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.shared.domain.SocialMediaAccount
import pl.zar.tikthrow.util.apiBaseUrl
import pl.zar.tikthrow.util.currentAuthToken
import pl.zar.tikthrow.util.metaOAuthStartUrl
import pl.zar.tikthrow.util.openExternalUrl
import pl.zar.tikthrow.util.tiktokOAuthStartUrl
import pl.zar.tikthrow.util.youtubeOAuthStartUrl

private val accountsJson = Json { ignoreUnknownKeys = true }

class AccountsViewModel : ViewModel() {
    val accounts: StateFlow<List<SocialMediaAccount>> = AccountsStore.accounts
    private val _youtubeStatusMessage = MutableStateFlow<String?>(null)
    val youtubeStatusMessage: StateFlow<String?> = _youtubeStatusMessage.asStateFlow()
    private val _tiktokStatusMessage = MutableStateFlow<String?>(null)
    val tiktokStatusMessage: StateFlow<String?> = _tiktokStatusMessage.asStateFlow()
    private val _metaStatusMessage = MutableStateFlow<String?>(null)
    val metaStatusMessage: StateFlow<String?> = _metaStatusMessage.asStateFlow()

    init {
        refreshYouTubeStatus()
        refreshTikTokStatus()
        refreshMetaStatus()
    }

    fun addAccount(platform: Platform, handle: String, displayName: String?) {
        AccountsStore.addAccount(platform, handle, displayName)
    }

    fun removeAccount(id: String) {
        val account = accounts.value.firstOrNull { it.id == id }
        when (account?.platform) {
            Platform.TIKTOK -> {
                removeTikTokAccount(id)
                return
            }
            Platform.FACEBOOK -> {
                removeFacebookAccount(id)
                return
            }
            Platform.INSTAGRAM -> {
                removeInstagramAccount(id)
                return
            }
            Platform.YOUTUBE -> Unit
            else -> {
                AccountsStore.removeAccount(id)
                return
            }
        }
        if (account?.platform != Platform.YOUTUBE) {
            AccountsStore.removeAccount(id)
            return
        }

        val token = currentAuthToken()
        if (token == null) {
            AccountsStore.removeAccount(id)
            _youtubeStatusMessage.value = "Zaloguj sie do aplikacji przed odlaczeniem YouTube"
            return
        }

        viewModelScope.launch {
            _youtubeStatusMessage.value = null
            val result = runCatching {
                val client = HttpClient { expectSuccess = false }
                val response = client.delete("$apiBaseUrl/api/oauth/youtube/$id") {
                    header(HttpHeaders.Authorization, "Bearer $token")
                }
                val body = response.bodyAsText()
                client.close()
                if (response.status.isSuccess()) {
                    AccountsStore.removeAccount(id)
                    _youtubeStatusMessage.value = "YouTube konto odlaczone"
                } else {
                    _youtubeStatusMessage.value = "Nie mozna odlaczyc YouTube: $body"
                }
            }
            result.getOrElse { exception ->
                _youtubeStatusMessage.value = "Nie mozna odlaczyc YouTube: ${exception.message}"
            }
        }
    }

    fun startYouTubeLogin(): Boolean {
        return openExternalUrl(youtubeOAuthStartUrl)
    }

    fun startTikTokLogin(): Boolean {
        return openExternalUrl(tiktokOAuthStartUrl)
    }

    fun startMetaLogin(): Boolean {
        return openExternalUrl(metaOAuthStartUrl)
    }

    private fun removeTikTokAccount(id: String) {
        val token = currentAuthToken()
        if (token == null) {
            AccountsStore.removeAccount(id)
            _tiktokStatusMessage.value = "Zaloguj sie do aplikacji przed odlaczeniem TikTok"
            return
        }
        viewModelScope.launch {
            _tiktokStatusMessage.value = null
            runCatching {
                val client = HttpClient { expectSuccess = false }
                val response = client.delete("$apiBaseUrl/api/oauth/tiktok/$id") {
                    header(HttpHeaders.Authorization, "Bearer $token")
                }
                client.close()
                if (response.status.isSuccess()) {
                    AccountsStore.removeAccount(id)
                    _tiktokStatusMessage.value = "TikTok konto odlaczone"
                } else {
                    _tiktokStatusMessage.value = "Nie mozna odlaczyc TikTok: ${response.bodyAsText()}"
                }
            }.getOrElse { exception ->
                _tiktokStatusMessage.value = "Nie mozna odlaczyc TikTok: ${exception.message}"
            }
        }
    }

    fun refreshTikTokStatus() {
        val token = currentAuthToken()
        if (token == null) {
            AccountsStore.markTikTokDisconnected()
            return
        }
        viewModelScope.launch {
            runCatching {
                val client = HttpClient { expectSuccess = false }
                val response = client.get("$apiBaseUrl/api/oauth/tiktok/accounts") {
                    header(HttpHeaders.Authorization, "Bearer $token")
                }
                val body = response.bodyAsText()
                client.close()
                if (response.status.isSuccess()) {
                    val tikTokAccounts = accountsJson.decodeFromString<List<TikTokAccountResponse>>(body)
                    val now = Clock.System.now()
                    AccountsStore.setTikTokAccounts(tikTokAccounts.map { it.toSocialMediaAccount(now) })
                } else {
                    AccountsStore.markTikTokDisconnected()
                }
            }.getOrElse {
                AccountsStore.markTikTokDisconnected()
            }
        }
    }

    fun refreshMetaStatus() {
        val token = currentAuthToken()
        if (token == null) {
            AccountsStore.markFacebookDisconnected()
            AccountsStore.markInstagramDisconnected()
            return
        }
        viewModelScope.launch {
            runCatching {
                val client = HttpClient { expectSuccess = false }
                val response = client.get("$apiBaseUrl/api/oauth/meta/accounts") {
                    header(HttpHeaders.Authorization, "Bearer $token")
                }
                val body = response.bodyAsText()
                client.close()
                if (response.status.isSuccess()) {
                    val metaAccounts = accountsJson.decodeFromString<MetaAccountsResponse>(body)
                    val now = Clock.System.now()
                    AccountsStore.setFacebookAccounts(metaAccounts.facebookAccounts.map { it.toSocialMediaAccount(now) })
                    AccountsStore.setInstagramAccounts(metaAccounts.instagramAccounts.map { it.toSocialMediaAccount(now) })
                } else {
                    AccountsStore.markFacebookDisconnected()
                    AccountsStore.markInstagramDisconnected()
                }
            }.getOrElse {
                AccountsStore.markFacebookDisconnected()
                AccountsStore.markInstagramDisconnected()
            }
        }
    }

    fun removeFacebookAccount(id: String) {
        val token = currentAuthToken()
        if (token == null) {
            AccountsStore.removeAccount(id)
            return
        }
        viewModelScope.launch {
            _metaStatusMessage.value = null
            runCatching {
                val client = HttpClient { expectSuccess = false }
                val response = client.delete("$apiBaseUrl/api/oauth/meta/facebook/$id") {
                    header(HttpHeaders.Authorization, "Bearer $token")
                }
                client.close()
                if (response.status.isSuccess()) {
                    AccountsStore.removeAccount(id)
                    _metaStatusMessage.value = "Facebook konto odlaczone"
                } else {
                    _metaStatusMessage.value = "Nie mozna odlaczyc Facebook: ${response.bodyAsText()}"
                }
            }.getOrElse { exception ->
                _metaStatusMessage.value = "Nie mozna odlaczyc Facebook: ${exception.message}"
            }
        }
    }

    fun removeInstagramAccount(id: String) {
        val token = currentAuthToken()
        if (token == null) {
            AccountsStore.removeAccount(id)
            return
        }
        viewModelScope.launch {
            _metaStatusMessage.value = null
            runCatching {
                val client = HttpClient { expectSuccess = false }
                val response = client.delete("$apiBaseUrl/api/oauth/meta/instagram/$id") {
                    header(HttpHeaders.Authorization, "Bearer $token")
                }
                client.close()
                if (response.status.isSuccess()) {
                    AccountsStore.removeAccount(id)
                    _metaStatusMessage.value = "Instagram konto odlaczone"
                } else {
                    _metaStatusMessage.value = "Nie mozna odlaczyc Instagram: ${response.bodyAsText()}"
                }
            }.getOrElse { exception ->
                _metaStatusMessage.value = "Nie mozna odlaczyc Instagram: ${exception.message}"
            }
        }
    }

    fun refreshYouTubeStatus() {
        val token = currentAuthToken()
        if (token == null) {
            AccountsStore.markYouTubeDisconnected()
            _youtubeStatusMessage.value = "Zaloguj sie do aplikacji przed laczeniem YouTube"
            return
        }
        viewModelScope.launch {
            _youtubeStatusMessage.value = null
            val result = runCatching {
                val client = HttpClient { expectSuccess = false }
                val response = client.get("$apiBaseUrl/api/oauth/youtube/accounts") {
                    header(HttpHeaders.Authorization, "Bearer $token")
                }
                val body = response.bodyAsText()
                client.close()

                if (response.status.isSuccess()) {
                    val ytAccounts = accountsJson.decodeFromString<List<YouTubeAccountResponse>>(body)
                    val now = Clock.System.now()
                    val socialAccounts = ytAccounts.map { it.toSocialMediaAccount(now) }
                    AccountsStore.setYouTubeAccounts(socialAccounts)
                    _youtubeStatusMessage.value = when {
                        ytAccounts.isEmpty() -> "YouTube nie jest polaczone"
                        ytAccounts.any { it.avatarUrl.isNullOrBlank() } -> "YouTube polaczone"
                        else -> "YouTube polaczone"
                    }
                } else {
                    AccountsStore.markYouTubeDisconnected()
                    _youtubeStatusMessage.value = "Nie mozna sprawdzic YouTube: $body"
                }
            }
            result.getOrElse { exception ->
                _youtubeStatusMessage.value = "Nie mozna sprawdzic YouTube: ${exception.message}"
            }
        }
    }
}

@Serializable
internal data class YouTubeAccountResponse(
    val accountId: String,
    val channelId: String,
    val displayName: String? = null,
    val avatarUrl: String? = null
)

private fun YouTubeAccountResponse.toSocialMediaAccount(now: kotlin.time.Instant): SocialMediaAccount =
    SocialMediaAccount(
        id = accountId,
        platform = Platform.YOUTUBE,
        accountHandle = channelId,
        displayName = displayName,
        avatarUrl = avatarUrl,
        isActive = true,
        createdAt = now,
        updatedAt = now
    )

@Serializable
internal data class TikTokAccountResponse(
    val accountId: String,
    val openId: String,
    val displayName: String? = null,
    val avatarUrl: String? = null
)

private fun TikTokAccountResponse.toSocialMediaAccount(now: kotlin.time.Instant): SocialMediaAccount =
    SocialMediaAccount(
        id = accountId,
        platform = Platform.TIKTOK,
        accountHandle = openId,
        displayName = displayName,
        avatarUrl = avatarUrl,
        isActive = true,
        createdAt = now,
        updatedAt = now
    )

@Serializable
internal data class MetaAccountsResponse(
    val facebookAccounts: List<FacebookAccountResponse>,
    val instagramAccounts: List<InstagramAccountResponse>,
)

@Serializable
internal data class FacebookAccountResponse(
    val accountId: String,
    val pageId: String,
    val displayName: String? = null,
    val avatarUrl: String? = null,
)

@Serializable
internal data class InstagramAccountResponse(
    val accountId: String,
    val igUserId: String,
    val displayName: String? = null,
    val avatarUrl: String? = null,
)

private fun FacebookAccountResponse.toSocialMediaAccount(now: kotlin.time.Instant): SocialMediaAccount =
    SocialMediaAccount(
        id = accountId,
        platform = Platform.FACEBOOK,
        accountHandle = pageId,
        displayName = displayName,
        avatarUrl = avatarUrl,
        isActive = true,
        createdAt = now,
        updatedAt = now
    )

private fun InstagramAccountResponse.toSocialMediaAccount(now: kotlin.time.Instant): SocialMediaAccount =
    SocialMediaAccount(
        id = accountId,
        platform = Platform.INSTAGRAM,
        accountHandle = igUserId,
        displayName = displayName,
        avatarUrl = avatarUrl,
        isActive = true,
        createdAt = now,
        updatedAt = now
    )
